import type { CredentialResponse } from "@react-oauth/google";

import * as React from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { Separator } from "@replo/design-system/components/separator/Separator";
import {
  Screen,
  useNextUrlWithPendingAppInstallationId,
} from "@v2/features/account/common";
import GoogleAuth from "@v2/features/account/GoogleAuth";
import { authRouter } from "@v2/trpc/routes/auth";
import { storeToken } from "@v2/utils/store-token";
import { useForm, useWatch } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { isEmpty } from "replo-utils/lib/misc";
import { z } from "zod";

const emailSchema = z.email();

type LoginFormValues = {
  email: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const handleLogin = (token: string) => {
    storeToken(token);
    window.location.replace(
      `${nextUrl ?? "/home"}${nextSearchParams ? `?${nextSearchParams}` : ""}`,
    );
  };

  const {
    mutate: loginApi,
    isPending: isLoadingLogin,
    error: loginError,
  } = authRouter.login.useMutation({
    onSuccess: ({ token }) => handleLogin(token),
  });

  const { mutate: loginWithGoogleApi, error: googleLoginError } =
    authRouter.loginWithGoogle.useMutation({
      onSuccess: ({ token }) => handleLogin(token),
    });

  // Note (Kevin, 2026-01-03, REPL-21631): Prefill test credentials on localhost
  const shouldPrefillTestCredentials = isLocalhost();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    clearErrors,
  } = useForm({
    defaultValues: {
      email: shouldPrefillTestCredentials
        ? "test@replo.app"
        : searchParams.get("email") ?? "",
      password: shouldPrefillTestCredentials ? "12345678" : "",
    },
    mode: "onBlur",
  });

  const watchAllFields = useWatch({ control });

  const handleGoogleCredentials = async (
    credentialResponse: CredentialResponse,
  ) => {
    const { credential, clientId } = credentialResponse;
    if (credential && clientId) {
      const pendingTemplateId = sessionStorage.getItem(
        "replo_pending_template_id",
      );
      loginWithGoogleApi({
        credential,
        clientId,
        ...(pendingTemplateId ? { pendingTemplateId } : {}),
      });
    }
  };

  const onSubmit = async ({ email, password }: LoginFormValues) => {
    const pendingTemplateId = sessionStorage.getItem(
      "replo_pending_template_id",
    );
    loginApi({
      email,
      rawPassword: password,
      ...(pendingTemplateId ? { pendingTemplateId } : {}),
    });
  };

  const emailError = !isEmpty(watchAllFields.email)
    ? errors?.email?.message
    : undefined;
  const passwordError = !isEmpty(watchAllFields.password)
    ? errors?.password?.message
    : undefined;

  const loginErrorMessage = loginError?.message ?? googleLoginError?.message;
  const shouldShowShopifyLoginInlineAlert =
    searchParams.get("error") === "login_in_to_associate_shopify";

  const removeSearchParam = () => {
    setSearchParams((previous) => {
      const newParams = new URLSearchParams(previous);
      newParams.delete("error");
      return newParams;
    });
  };

  return (
    <form
      className="no-scrollbar"
      onSubmit={(data) => {
        if (shouldShowShopifyLoginInlineAlert) {
          removeSearchParam();
        }
        void handleSubmit(onSubmit)(data);
      }}
    >
      {(loginErrorMessage || shouldShowShopifyLoginInlineAlert) && (
        <Alert
          variant={
            shouldShowShopifyLoginInlineAlert && !loginErrorMessage
              ? "warning"
              : "error"
          }
          UNSAFE_className="mb-2"
          description={
            loginErrorMessage ??
            "Please create or log in to your Replo account to continue to Replo."
          }
        />
      )}
      <div className="flex flex-col gap-4">
        <LabeledControl label="Email" size="base" id="email" error={emailError}>
          <Input
            id="email"
            invalid={Boolean(emailError)}
            autoComplete="off"
            placeholder="Your Work Email"
            {...register("email", {
              required: "Please enter a valid email address.",
              validate: (value) => {
                const parsedEmail = emailSchema.safeParse(value);
                return (
                  parsedEmail.success || "Please enter a valid email address"
                );
              },
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("email"),
            })}
            type="email"
            size="base"
          />
        </LabeledControl>
        <LabeledControl
          label="Password"
          id="password"
          labelAction={
            <Link
              to={Screen.INIT_RESET_PASSWORD}
              className="text-primary text-sm font-medium hover:underline"
            >
              Forgot Password?
            </Link>
          }
          size="base"
          error={passwordError}
        >
          <Input
            id="password"
            autoComplete="off"
            placeholder="••••••••••••"
            invalid={Boolean(passwordError)}
            {...register("password", {
              required: "Please enter a valid password",
              // NOTE (Fran 2024-02-27): We should clear the errors when the user is typing
              onChange: () => clearErrors("password"),
            })}
            type="password"
            size="base"
          />
        </LabeledControl>
      </div>
      <div className="mt-10 flex flex-col gap-y-4 overflow-hidden">
        <Button
          className="w-full"
          variant="primary"
          size="base"
          type="submit"
          loading={isLoadingLogin}
        >
          Login
        </Button>
        <Separator />
        <GoogleAuth handleGoogleCredentials={handleGoogleCredentials} />
      </div>
    </form>
  );
};

export default LoginForm;
